import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { KeyRound, Loader2, Trash2, UserPlus } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Field, inputCls } from "../../components/forms/Field";
import { api, errMsg, formatDate } from "../../lib/api";

const PW_HINT = "At least 8 characters, with a letter and a number.";

const AddStaff = ({ onDone }) => {
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/admin/team", f);
      toast.success("Staff account created. Share the email and password with them.");
      setF({ name: "", email: "", password: "" }); onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6 lg:col-span-2" data-testid="staff-add-form">
      <h3 className="font-display font-bold text-navy">Add staff account</h3>
      <Field label="Full name" required><Input className={inputCls} required value={f.name} onChange={set("name")} data-testid="staff-name-input" /></Field>
      <Field label="Email" required><Input className={inputCls} type="email" required value={f.email} onChange={set("email")} data-testid="staff-email-input" /></Field>
      <Field label="Temporary password" required hint={PW_HINT}><Input className={inputCls} type="text" required minLength={8} value={f.password} onChange={set("password")} data-testid="staff-password-input" /></Field>
      <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="staff-add-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}Create account</button>
    </form>
  );
};

const ResetDialog = ({ user, onClose }) => {
  const [pw, setPw] = useState("");
  const save = async (e) => {
    e.preventDefault();
    try { await api.patch(`/admin/team/${user.id}`, { password: pw }); toast.success("Password reset. They have been signed out."); setPw(""); onClose(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="staff-reset-dialog">
        <DialogHeader><DialogTitle className="text-navy">Reset password – {user?.name}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="New password" required hint={PW_HINT}><Input className={inputCls} required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="staff-reset-input" /></Field>
          <button type="submit" className="btn-primary w-full" data-testid="staff-reset-save">Save new password</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const TeamTab = ({ me }) => {
  const qc = useQueryClient();
  const [reset, setReset] = useState(null);
  const { data = [] } = useQuery({ queryKey: ["admin-team"], queryFn: () => api.get("/admin/team").then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-team"] });
  const toggle = async (u) => { try { await api.patch(`/admin/team/${u.id}`, { active: !u.active }); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name}'s account?`)) return;
    try { await api.delete(`/admin/team/${u.id}`); toast.success("Account deleted"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-5">
      <AddStaff onDone={refresh} />
      <div className="space-y-3 lg:col-span-3">
        {data.map((u, i) => (
          <div key={u.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`team-row-${i}`}>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-navy">{u.name} {u.id === me.id && <span className="text-xs font-normal text-slate-400">(you)</span>}</p>
              <p className="text-xs text-slate-500">{u.email} · {u.role === "admin" ? "Main admin" : "Staff"} · added {formatDate(u.created_at)}</p>
            </div>
            {u.role === "staff" && (
              <>
                <label className="flex items-center gap-2 text-xs"><Switch checked={u.active !== false} onCheckedChange={() => toggle(u)} data-testid={`team-active-${i}`} />{u.active !== false ? "Active" : "Disabled"}</label>
                <button onClick={() => setReset(u)} className="rounded p-2 hover:bg-slate-100" title="Reset password" data-testid={`team-reset-${i}`}><KeyRound className="h-4 w-4 text-royal" /></button>
                <button onClick={() => del(u)} className="rounded p-2 hover:bg-red-50" title="Delete" data-testid={`team-delete-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></button>
              </>
            )}
          </div>
        ))}
      </div>
      <ResetDialog user={reset} onClose={() => setReset(null)} />
    </div>
  );
};

export const ChangePasswordButton = () => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/change-password", f); toast.success("Password changed"); setF({ current_password: "", new_password: "" }); setOpen(false); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="flex items-center gap-1 rounded-lg bg-white/10 px-3 py-1.5 hover:bg-white/20" data-testid="change-password-button"><KeyRound className="h-4 w-4" /><span className="hidden sm:inline">Password</span></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader><DialogTitle className="text-navy">Change your password</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Current password" required><Input className={inputCls} type="password" required value={f.current_password} onChange={(e) => setF((s) => ({ ...s, current_password: e.target.value }))} data-testid="current-password-input" /></Field>
            <Field label="New password" required hint={PW_HINT}><Input className={inputCls} type="password" required minLength={8} value={f.new_password} onChange={(e) => setF((s) => ({ ...s, new_password: e.target.value }))} data-testid="new-password-input" /></Field>
            <button type="submit" className="btn-primary w-full" data-testid="change-password-save">Update password</button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
